import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  Table,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useTableHorizontalScrollEnd } from "hooks/useTableHorizontalScrollEnd";
import {
  findTableColumnDefinitionByPreferenceKey,
  formatDateTimeValueForTable,
  getTableColumnPreferenceKey,
  idKey,
  incentiveType,
  normalizeYesNoDisplayLabel,
  onBoardingStatusType,
  phoneNumberShowFormat,
  quotaType,
  renderCallExecutiveTableBodyCell,
  resolveCallExecutiveExportCellValue,
  resolveCommonTableKey,
  getCommonTableVariant,
  statusKey,
  statusType,
  DEFAULT_PAGE_SIZE,
} from "helpers/GeneralHelper";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useTableColumnPreferences } from "libs/context/TableColumnPreferencesContext";
import { useTablePageSizePreference } from "libs/context/TablePageSizePreferencesContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import ManageColumnsModalFields from "components/Common/ManageColumnsModalFields";
import StatusMetadataBadge from "components/Common/StatusMetadataBadge";
import MaskedIdTableCell from "components/Common/MaskedIdTableCell";
import {
  ALL_ROWS_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
  buildPageSizeOptions,
  buildPaginationPageItems,
  formatPageSizeLabel,
  PAGINATION_ELLIPSIS,
  resolvePageSizeDisplayRange,
  scrollTablePaginationToTop,
} from "components/Common/ServerListPagination";
import TableFilterToolbarActions from "components/Common/TableFilterToolbarActions";
import SpecialNotesModal from "pages/AllCommonModal/SpecialNotesModal/special-notes-modal";
import {
  isCandidateStatusMetadataAccessor,
  normalizeCandidateStatusMetadataValue,
} from "helpers/statusMetadataHelpers";

const SPECIAL_NOTES_NAME_CLICK_PAGE_NAMES = new Set([
  "inprogressreport",
  "rtrinternalreport",
  "clientreport",
  "candidatesummaryreport",
]);

const resolveRowSpecialNotes = (row = {}) =>
  String(row?.specialNotes ?? row?.specialNote ?? row?.special_notes ?? "").trim();

const rowHasSpecialNote = (row = {}) =>
  Boolean(
    resolveRowSpecialNotes(row) || row?.hasSpecialNote || row?.isSpecialNote
  );

const normalizeDownloadLinkColumnAccessor = (accessor = "") =>
  String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");

const isDownloadLinkColumnAccessor = (accessor) => {
  const normalized = normalizeDownloadLinkColumnAccessor(accessor);
  return (
    normalized === "video" ||
    normalized === "vlink" ||
    normalized === "cvlink" ||
    normalized === "mecimagelink"
  );
};

/** Client table — name / email / fullname / IP accent (`theme.scss`). */
const CLIENT_NAME_OR_EMAIL_ACCESSORS = ["name", "email", "fullname", "ip", "ipaddress", "ip_address"];
/** Team table — email only. */
const TEAM_NAME_OR_EMAIL_ACCESSORS = ["email"];
/** Cell renderers: use `.includes(accessor)` instead of long `a === x || …` chains. */
const TEAM_STATUS_BADGE_ACCESSORS = ["status", "lastStatus"];
const TABLE_STATUS_BADGE_CLASS = "px-2 py-1 fw-semibold common-table-status-badge";

const isClientNameOrEmailColumnAccessor = (accessor) => {
  const a = String(accessor ?? "").trim().toLowerCase();
  return CLIENT_NAME_OR_EMAIL_ACCESSORS.includes(a);
};

const isTeamNameOrEmailColumnAccessor = (accessor) => {
  const a = String(accessor ?? "").trim().toLowerCase();
  return TEAM_NAME_OR_EMAIL_ACCESSORS.includes(a);
};

const isCommonNameOrEmailColumnAccessor = (accessor) => {
  const normalized = String(accessor ?? "").trim().toLowerCase();
  return (
    normalized === "name" ||
    normalized === "email" ||
    normalized === "fullname" ||
    normalized === "ip" ||
    normalized === "ipaddress" ||
    normalized === "ip_address"
  );
};

const CLIENT_TABLE_CLIENT_COMPANY_ACCESSORS = [
  "client",
  "clientname",
  "company",
  "companyname",
];

const isClientTableClientCompanyColumnAccessor = (accessor) => {
  const a = String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");
  return CLIENT_TABLE_CLIENT_COMPANY_ACCESSORS.includes(a);
};

const isCommonTableClientCompanyColumnAccessor = (accessor) => {
  const normalized = String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");
  return (
    normalized === "client" ||
    normalized === "clientname" ||
    normalized === "company" ||
    normalized === "companyname"
  );
};

const isPhoneColumnAccessor = (accessor) =>
  String(accessor ?? "").trim().toLowerCase().includes("phone");

/** Plain-text column title for COPY/CSV/Excel/PDF export (Header may be a React node). */
const getColumnExportHeaderLabel = (col = {}) => {
  if (col?.exportHeader != null && String(col.exportHeader).trim() !== "") {
    return String(col.exportHeader).trim();
  }
  if (col?.keyName != null && String(col.keyName).trim() !== "") {
    return String(col.keyName).trim();
  }
  if (typeof col?.Header === "string" && col.Header.trim() !== "") {
    return col.Header.trim();
  }
  return String(col?.accessor ?? "Column");
};

const mergeDataCellClasses = (accessor, variant) => {
  if (variant === "client") {
    return (
      [
        isClientNameOrEmailColumnAccessor(accessor) && "common-client-table-name-email-cell",
        isClientTableClientCompanyColumnAccessor(accessor) && "common-client-table-client-company-cell",
      ]
        .filter(Boolean)
        .join(" ") || undefined
    );
  }
  if (variant === "common") {
    return (
      [
        isCommonNameOrEmailColumnAccessor(accessor) && "common-table-name-email-cell",
        isCommonTableClientCompanyColumnAccessor(accessor) && "common-table-client-company-cell",
      ]
        .filter(Boolean)
        .join(" ") || undefined
    );
  }
  return (
    [isTeamNameOrEmailColumnAccessor(accessor) && "common-client-table-name-email-cell"]
      .filter(Boolean)
      .join(" ") || undefined
  );
};

const STATUS_STYLE_ACCESSORS = ["status", "lastStatus", "statusId"];

const isColumnStatusType = (columnType) =>
  String(columnType ?? "").trim().toLowerCase() === "status";

const isClientTableStatusColumn = (col = {}) =>
  STATUS_STYLE_ACCESSORS.includes(col?.accessor) || isColumnStatusType(col?.type);
const ONBOARDING_STYLE_ACCESSORS = ["type", "adSpend", "plan", "subscribed"];
const QUOTA_STYLE_ACCESSORS = [
  "quota",
  "teamRequired",
  "uniqueClient",
  "multipleTeam",
  "candidateDeactive",
  "forTools",
  "isRegister",
  "isBackend",
];
const TEAM_QUOTA_STYLE_BOOLEAN_ACCESSORS = [
  "quota",
  "candidateDeactive",
  "forTools",
  "isRegister",
  "approved",
  "online",
  "blocked",
];
const STATUS_METADATA_BADGE_ACCESSORS = ["approved", "incentive"];
const FOR_CLIENT_JOB_ACCESSORS = ["forClient", "forJob"];
const TEAM_FOR_CLIENT_JOB_ACCESSORS = ["forClient", "forJob"];
const NOTES_TEXTAREA_ACCESSORS = ["notes", "fedex_id"];

const renderNotesTextareaCell = (cellValue, rowRecord = {}, col = {}, onNotesBlur) => (
  <textarea
    className="form-control textarea-width"
    rows={1}
    defaultValue={cellValue}
    onBlur={
      NOTES_TEXTAREA_ACCESSORS.includes(col?.accessor) &&
      typeof onNotesBlur === "function"
        ? (event) =>
            onNotesBlur(rowRecord, event.target.value, col?.accessor)
        : undefined
    }
  />
);
const IS_ALLOWED_ACCESSORS = new Set(["is_allowed", "isAllowed", "isBlock", "isBlocked"]);

const isToggleCheckboxColumnAccessor = (accessor = "") => {
  const normalized = String(accessor ?? "").replace(/_/g, "").toLowerCase();
  return (
    IS_ALLOWED_ACCESSORS.has(accessor) ||
    normalized === "isallowed" ||
    normalized === "isblocked" ||
    normalized === "isblock"
  );
};

const getToggleCheckboxColumnWidthStyle = (accessor) => {
  if (!isToggleCheckboxColumnAccessor(accessor)) return undefined;
  return { minWidth: "72px", width: "72px", maxWidth: "72px" };
};

const renderIsAllowedCheckbox = (item, col, cellValue, onToggleAllowed) => {
  if (!isToggleCheckboxColumnAccessor(col?.accessor) || !onToggleAllowed) return null;
  return (
    <div className="d-flex justify-content-center">
      <input
        type="checkbox"
        className="form-check-input"
        defaultChecked={!!cellValue}
        onChange={(changeEvent) => onToggleAllowed(item, changeEvent.target.checked)}
      />
    </div>
  );
};

const normalizeQuotaStyleAccessorKey = (accessor = "") =>
  String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");

const isQuotaStyleYesNoAccessor = (accessor) =>
  QUOTA_STYLE_ACCESSORS.some(
    (key) => normalizeQuotaStyleAccessorKey(key) === normalizeQuotaStyleAccessorKey(accessor)
  );

const isWeeklyReviewRagStatusAccessor = (accessor) => {
  const normalized = String(accessor ?? "").replace(/_/g, "").toLowerCase();
  return (
    normalized === "status" ||
    normalized === "statusthisweek" ||
    normalized === "statuslastweek" ||
    normalized === "thisweekstatus" ||
    normalized === "lastweekstatus"
  );
};

const isStatusMetadataBadgeAccessor = (accessor) => {
  const normalized = String(accessor ?? "").replace(/_/g, "").toLowerCase();
  return (
    normalized === "status" ||
    normalized === "statusid" ||
    normalized === "statustitle" ||
    normalized === "statusthisweek" ||
    normalized === "statuslastweek" ||
    normalized === "thisweekstatus" ||
    normalized === "lastweekstatus" ||
    isCandidateStatusMetadataAccessor(accessor)
  );
};

const isCommonTableStatusMetadataColumn = (col = {}, pageNameNorm = "") => {
  if (isStatusMetadataBadgeAccessor(col?.accessor)) return true;
  if (pageNameNorm === "candidateonboarding" && isColumnStatusType(col?.type)) {
    return true;
  }
  if (pageNameNorm === "attendancereport" && isColumnStatusType(col?.type)) {
    return true;
  }
  return false;
};

const isStatusClientTableColumnAccessor = (accessor) => {
  if (STATUS_STYLE_ACCESSORS.includes(accessor)) return true;
  const normalized = String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");
  if (normalized === "status" || normalized === "statusid" || normalized === "laststatus") {
    return true;
  }
  if (statusKey(accessor) === "status") return true;
  return isWeeklyReviewRagStatusAccessor(accessor);
};

const isStatusTeamTableColumnAccessor = (accessor) => {
  if (TEAM_STATUS_BADGE_ACCESSORS.includes(accessor)) return true;
  const normalized = String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");
  if (
    normalized === "status" ||
    normalized === "laststatus" ||
    normalized === "callstatus" ||
    normalized === "statuslabel"
  ) {
    return true;
  }
  if (statusKey(accessor) === "status") return true;
  return isWeeklyReviewRagStatusAccessor(accessor);
};

const isIdTableColumnAccessor = (accessor) =>
  String(accessor ?? "").trim().toLowerCase().replace(/_/g, "") === "id" ||
  idKey(accessor) === "id";

const isCenterAlignedFlagTableColumnAccessor = (accessor) => {
  const normalized = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  return (
    normalized === "forclient" ||
    normalized === "forjob" ||
    normalized === "candidatedeactive" ||
    normalized === "fortools" ||
    normalized === "isforuscitizens" ||
    normalized === "isfornonuscitizens" ||
    normalized === "isallowed" ||
    normalized === "isblock" ||
    normalized === "isblocked" ||
    normalized === "isdefault"
  );
};

const isCenterAlignedClientTableColumnAccessor = (accessor) => {
  const normalized = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  if (
    normalized === "status" ||
    normalized === "statusid" ||
    normalized === "approved" ||
    normalized === "timezone" ||
    normalized === "laststatus" ||
    normalized === "link" ||
    normalized === "cvlink" ||
    normalized === "mecimagelink" ||
    normalized === "video" ||
    normalized === "vlink" ||
    normalized === "url" ||
    normalized === "type" ||
    normalized === "adspend" ||
    normalized === "plan"
  ) {
    return true;
  }
  if (STATUS_STYLE_ACCESSORS.includes(accessor)) return true;
  if (statusKey(accessor) === "status") return true;
  return isWeeklyReviewRagStatusAccessor(accessor);
};

const isPageSpecificCenterAlignedColumnAccessor = (accessor, pageName) => {
  const normalizedPage = String(pageName || "").trim().toLowerCase().replace(/\s+/g, "");
  const normalizedAccessor = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  if (
    (normalizedPage === "job" ||
      normalizedPage === "source" ||
      normalizedPage === "docrequired") &&
    normalizedAccessor === "name"
  ) {
    return true;
  }
  if (normalizedPage === "legends" && normalizedAccessor === "title") {
    return true;
  }
  return false;
};

/** Call Manager Ready To Send — wider call status column. */
const isReadyToSendCallStatusColumnAccessor = (accessor, pageName) => {
  const normalizedPage = String(pageName || "").trim().toLowerCase().replace(/\s+/g, "");
  if (normalizedPage !== "callmanagerreadytosend") return false;
  const normalizedAccessor = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  return (
    normalizedAccessor === "callstatus" || normalizedAccessor === "laststatusstack"
  );
};

const isCenterAlignedTableColumnAccessor = (accessor, pageName, variant) => {
  if (variant === "client") return isCenterAlignedClientTableColumnAccessor(accessor);
  if (variant === "common") {
    return (
      isStatusMetadataBadgeAccessor(accessor) ||
      isCenterAlignedFlagTableColumnAccessor(accessor) ||
      isPageSpecificCenterAlignedColumnAccessor(accessor, pageName)
    );
  }
  return isStatusTeamTableColumnAccessor(accessor);
};

const isStatusTableColumnAccessor = (accessor, variant) => {
  if (variant === "client") return isStatusClientTableColumnAccessor(accessor);
  if (variant === "common") return isStatusMetadataBadgeAccessor(accessor);
  return isStatusTeamTableColumnAccessor(accessor);
};

const getTableStatusColClass = (col = {}, pageName = "", variant = "") => {
  const accessor = col?.accessor ?? col;
  if (!isStatusTableColumnAccessor(accessor, variant)) return "";
  return col?.statusColClass || "common-table-status-col";
};

const getTableColumnHeaderClassName = (col, pageName, variant) => {
  const accessor = col?.accessor ?? col;
  const classNames = col?.headerWrap
    ? ["candidate-onboarding-header-wrap"]
    : ["text-nowrap"];
  if (isCenterAlignedTableColumnAccessor(accessor, pageName, variant)) {
    classNames.push("text-center");
  } else {
    classNames.push("text-start");
  }
  if (isIdTableColumnAccessor(accessor)) {
    classNames.push("common-table-id-col");
  }
  const statusColClass = getTableStatusColClass(col, pageName, variant);
  if (statusColClass) classNames.push(statusColClass);
  if (isReadyToSendCallStatusColumnAccessor(accessor, pageName)) {
    classNames.push("ces-ready-to-send-status-wide");
  }
  return classNames.join(" ");
};

const isGrandTotalSummaryRow = (item) => Boolean(item?._isGrandTotalRow);

const normalizeTableBodyAccessor = (accessor) =>
  String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");

const getGrandTotalSummaryCellClassName = (item, accessor) => {
  if (!isGrandTotalSummaryRow(item)) return "";
  const norm = normalizeTableBodyAccessor(accessor);
  if (norm === "callstage" || norm === "stage" || norm === "totalcalltrigger" || norm === "grandtotal") {
    return "text-start fw-bold";
  }
  return "";
};

const getTableColumnCellClassName = (col, pageName, variant, item) => {
  const accessor = col?.accessor ?? col;
  const classNames = ["align-middle"];
  if (isCenterAlignedTableColumnAccessor(accessor, pageName, variant)) {
    classNames.push("text-center");
  } else {
    classNames.push("text-start");
  }
  if (isIdTableColumnAccessor(accessor)) {
    classNames.push("common-table-id-col");
  }
  const statusColClass = getTableStatusColClass(col, pageName, variant);
  if (statusColClass) classNames.push(statusColClass);
  if (isReadyToSendCallStatusColumnAccessor(accessor, pageName)) {
    classNames.push("ces-ready-to-send-status-wide");
  }
  const dataCellClassName = mergeDataCellClasses(accessor, variant);
  if (dataCellClassName) classNames.push(dataCellClassName);
  const grandTotalCellClassName = getGrandTotalSummaryCellClassName(item, accessor);
  if (grandTotalCellClassName) classNames.push(grandTotalCellClassName);
  return classNames.join(" ");
};

const isYesNoBadgeTableColumnAccessor = (accessor) => {
  const normalized = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  return (
    normalized === "quota" ||
    normalized === "forclient" ||
    normalized === "forjob" ||
    normalized === "candidatedeactive" ||
    normalized === "fortools" ||
    normalized === "isforuscitizens" ||
    normalized === "isfornonuscitizens" ||
    normalized === "isdefault"
  );
};

const resolveYesNoBadgeLabel = (rawValue) =>
  normalizeYesNoDisplayLabel(rawValue) ?? "No";

const resolveStatusTableCellValue = (dataRow, accessor) => {
  const normalized = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  if (normalized === "status" || normalized === "statusid" || normalized === "statustitle") {
    return dataRow?.statusId ?? dataRow?.status ?? dataRow?.statusTitle ?? "";
  }
  return dataRow?.[accessor];
};

const resolveStatusTableFallbackClass = (dataRow, accessor) => {
  const normalized = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  const resolvedValue =
    normalized === "status" || normalized === "statusid" || normalized === "statustitle"
      ? dataRow?.statusId ?? dataRow?.status ?? dataRow?.statusTitle ?? ""
      : dataRow?.[accessor] ?? "";

  return statusType(String(resolvedValue).trim().toLowerCase());
};

const isRowStatusLabelCssBadgeAccessor = (accessor) => {
  const normalized = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  return (
    normalized === "status" ||
    normalized === "statuslabel" ||
    normalized === "statusid" ||
    normalized === "statustitle"
  );
};

const isReadyToSendStatusBadgeAccessor = (accessor) => {
  const normalized = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  return normalized === "readytosend";
};

const renderReadyToSendStatusMetadataBadge = (dataRow, accessor, pageName = "") => {
  if (!isReadyToSendStatusBadgeAccessor(accessor)) return null;

  const label = String(
    dataRow?.readyToSendStatusLabel ?? dataRow?.ready_to_send ?? ""
  ).trim();
  if (!label) return null;

  const statusColor = String(dataRow?.readyToSendStatusColor ?? "").trim();
  if (/^#[0-9A-Fa-f]{3,8}$/.test(statusColor)) {
    return (
      <span className={TABLE_STATUS_BADGE_CLASS} style={{ backgroundColor: statusColor, color: "#fff" }}>
        {label}
      </span>
    );
  }

  return (
    <StatusMetadataBadge
      accessor="readyToSendStatus"
      pageName={pageName}
      value={label}
      fallbackClassName={statusType(label)}
    />
  );
};

const renderRowStatusLabelMetadataBadge = (dataRow, accessor, pageName = "") => {
  if (!isRowStatusLabelCssBadgeAccessor(accessor)) return null;

  const label = String(dataRow?.statusLabel ?? "").trim();
  if (!label) return null;

  const statusCss = String(dataRow?.statusCss ?? "").trim();
  const normalizedAccessor = String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");

  return (
    <StatusMetadataBadge
      accessor={normalizedAccessor === "statustitle" ? "status" : accessor}
      pageName={pageName}
      value={label}
      fallbackClassName={statusCss || statusType(label)}
    />
  );
};

const TableContainer = ({ columns,
  data,
  isActionRequired,
  onEdit,
  getEditHref,
  onDelete,
  onCopy,
  onDuplicate,
  onToggleAllowed,
  onView,
  onDetails,
  isEditAccess,
  isViewAccess,
  isCallDetailsAccess,
  isDeleteAccess,
  isCheckboxLeftSide,
  isPagination,
  pageName,
  tableKey: tableKeyProp,
  currentPage: controlledCurrentPage,
  rowsPerPage: controlledRowsPerPage,
  totalRecords: controlledTotalRecords,
  totalPages: controlledTotalPages,
  onPageChange,
  onPageSizeChange,
  setCommonFilterModalOpen,
  setCommonResetFilter,
  allCommonFilter,
  /** Number of active modal/metadata filters — shown above the FILTERS control. */
  activeFilterCount = 0,
  onBill,
  onPermalink,
  onOffboarding,
  onIncentiveTarget,
  onJobDescription,
  onAppreciation,
  onPreQuestioning,
  onAdvantageLogin,
  /** Manage User row actions (with `pageName="ManageUser"`). */
  onClientAccess,
  onTemporaryClientAccess,
  onPermission,
  onResetBlock,
  /** AI Recruiter log (`pageName="AiRecruiterLog"`). */
  onAiRecruiterCallTranscript,
  onAiRecruiterRawJson,
  /** Optional row selection (left checkbox column). */
  enableRowSelection = false,
  resolveRowId,
  bulkActionLabel = "Reset all selected",
  onBulkAction,
  /** Notifies parent when row checkbox selection changes (`string[]` row ids). */
  onSelectionChange,
  /** `(rowRecord, notesValue) => void` — notes column blur. */
  onNotesBlur,
  /** Call executive schedule (`pageName="CallExecutiveSchedule"`). */
  onCallExecutiveCall,
  onCallExecutiveMessage,
  onCallExecutiveVideoInterview,
  /** When true, video interview icon only shows if row has `candidateVideoInterviewLink`. */
  callExecutiveVideoInterviewRequiresLink = false,
  onCallExecutiveMarkEligible,
  onCallExecutiveResolved,
  onCallExecutiveQuickUpdateBlur,
  /** Candidate onboarding (`pageName="CandidateOnboarding"`). */
  onOnboardingDocument,
  /** Account executive weekly review → candidates pipeline shortcut. */
  onPipeline,
  showCopyCsvPrintToolbar = false,
}) => {
  const pageNameNorm = String(pageName || "").toLowerCase();
  const tableVariant = getCommonTableVariant(pageName);
  const isCallExecutiveTable = tableVariant === "call-executive";
  const isClientStyleTable = tableVariant === "client";

  const resolvedTableKey = useMemo(
    () => resolveCommonTableKey(pageName, tableKeyProp),
    [tableKeyProp, pageName]
  );

  const { scrollRef: tableScrollRef, isScrolledToEnd } = useTableHorizontalScrollEnd();

  const {
    columnOrder,
    visibleColumnOrder,
    orderedColumns,
    visibleColumns,
    handleColumnDragEnd,
    handleColumnToggle,
  } = useTableColumnPreferences(resolvedTableKey, columns);
  const isServerPagination = typeof onPageChange === "function";
  const defaultClientPageSize = Number(controlledRowsPerPage) || 5;
  const { pageSize: preferredPageSize, setPageSize: persistPageSize, settingsReady } =
    useTablePageSizePreference(
      resolvedTableKey,
      isServerPagination ? Number(controlledRowsPerPage) || DEFAULT_PAGE_SIZE : defaultClientPageSize
    );
  const isManageUserTable = pageNameNorm === "manageuser";
  const isCompanyUserTable = pageNameNorm === "companyuser";
  const { permissionData } = usePermissionContext() || {};
  const canShowAllRows = Boolean(permissionData?.CandidateShowAllAccess);
  const [rowsPerPage, setRowsPerPage] = useState(() =>
    isServerPagination
      ? Number(controlledRowsPerPage) || preferredPageSize
      : preferredPageSize
  );
  const [currentPage, setCurrentPage] = useState(controlledCurrentPage || 1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
  const hasAppliedPersistedPageSizeRef = useRef(false);

  useEffect(() => {
    if (controlledCurrentPage) {
      setCurrentPage(controlledCurrentPage);
    }
  }, [controlledCurrentPage]);

  useEffect(() => {
    if (Number.isFinite(Number(controlledRowsPerPage))) {
      setRowsPerPage(Number(controlledRowsPerPage));
    }
  }, [controlledRowsPerPage]);

  useEffect(() => {
    if (!isServerPagination || !settingsReady || hasAppliedPersistedPageSizeRef.current) return;

    const controlledSize = Number(controlledRowsPerPage);
    let nextPreferred = Number(preferredPageSize);

    // Saved All without permission — keep current size, do not refetch.
    if (nextPreferred === ALL_ROWS_PAGE_SIZE && !canShowAllRows) {
      hasAppliedPersistedPageSizeRef.current = true;
      return;
    }

    // Preference is All but parent/API already settled on a positive page size (e.g. 200).
    // Accept API value, update saved preference, and do NOT request All again (avoids remount loop).
    if (
      nextPreferred === ALL_ROWS_PAGE_SIZE &&
      Number.isFinite(controlledSize) &&
      controlledSize > 0
    ) {
      hasAppliedPersistedPageSizeRef.current = true;
      if (controlledSize !== nextPreferred) {
        persistPageSize(controlledSize);
      }
      return;
    }

    hasAppliedPersistedPageSizeRef.current = true;
    if (
      Number.isFinite(nextPreferred) &&
      nextPreferred !== controlledSize &&
      typeof onPageSizeChange === "function"
    ) {
      onPageSizeChange(nextPreferred);
    }
  }, [
    isServerPagination,
    settingsReady,
    preferredPageSize,
    controlledRowsPerPage,
    onPageSizeChange,
    canShowAllRows,
    persistPageSize,
  ]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [visibleApiKeyMap, setVisibleApiKeyMap] = useState({});

  const handleSort = (accessor) => {
    setSortConfig((prev) => {
      if (prev.key === accessor) {
        return {
          key: accessor,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key: accessor, direction: "asc" };
    });
  };

  const toggleApiKeyVisibility = (rowKey, accessor) => {
    const visibilityKey = `${rowKey}-${accessor}`;
    setVisibleApiKeyMap((prev) => ({
      ...prev,
      [visibilityKey]: !prev?.[visibilityKey],
    }));
  };

  const sortedData = React.useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    const summaryRows = rows.filter(isGrandTotalSummaryRow);
    const bodyRows = rows.filter((row) => !isGrandTotalSummaryRow(row));

    if (!sortConfig.key) return [...bodyRows, ...summaryRows];

    const sortedBody = [...bodyRows].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    return [...sortedBody, ...summaryRows];
  }, [data, sortConfig]);

  const isAllRowsPageSize = Number(rowsPerPage) === ALL_ROWS_PAGE_SIZE;
  const totalPages = isServerPagination
    ? controlledTotalPages || 1
    : isAllRowsPageSize
      ? 1
      : Math.ceil(sortedData.length / rowsPerPage) || 1;
  const startIndex = isAllRowsPageSize ? 0 : (currentPage - 1) * rowsPerPage;
  const paginatedData = isPagination
    ? isServerPagination || isAllRowsPageSize
      ? sortedData
      : sortedData.slice(startIndex, startIndex + rowsPerPage)
    : sortedData;

  const defaultResolveRowId = (row = {}) =>
    String(row?.id ?? row?._id ?? row?.rowId ?? "").trim();
  const getRowId =
    typeof resolveRowId === "function" ? resolveRowId : defaultResolveRowId;

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const headerCheckboxRef = useRef(null);

  const pageRowIds = useMemo(
    () =>
      enableRowSelection && isCheckboxLeftSide
        ? (paginatedData || []).map((item) => getRowId(item)).filter(Boolean)
        : [],
    [paginatedData, enableRowSelection, isCheckboxLeftSide, resolveRowId]
  );

  const allPageSelected =
    enableRowSelection &&
    isCheckboxLeftSide &&
    pageRowIds.length > 0 &&
    pageRowIds.every((id) => selectedIds.has(id));
  const somePageSelected =
    enableRowSelection &&
    isCheckboxLeftSide &&
    pageRowIds.some((id) => selectedIds.has(id));

  useEffect(() => {
    if (!enableRowSelection || !isCheckboxLeftSide) return;
    setSelectedIds(new Set());
  }, [
    enableRowSelection,
    isCheckboxLeftSide,
    controlledCurrentPage,
    controlledRowsPerPage,
  ]);

  useEffect(() => {
    if (!headerCheckboxRef.current || !enableRowSelection || !isCheckboxLeftSide) return;
    headerCheckboxRef.current.indeterminate = somePageSelected && !allPageSelected;
  }, [somePageSelected, allPageSelected, enableRowSelection, isCheckboxLeftSide]);

  useEffect(() => {
    if (!enableRowSelection || typeof onSelectionChange !== "function") return;
    onSelectionChange(Array.from(selectedIds));
  }, [selectedIds, enableRowSelection, onSelectionChange]);

  const toggleRowId = (id) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSel =
        pageRowIds.length > 0 && pageRowIds.every((rowId) => next.has(rowId));
      if (allSel) {
        pageRowIds.forEach((rowId) => next.delete(rowId));
      } else {
        pageRowIds.forEach((rowId) => next.add(rowId));
      }
      return next;
    });
  };

  const handleBulkActionClick = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length || typeof onBulkAction !== "function") return;
    const shouldClearSelection = await onBulkAction(ids);
    if (shouldClearSelection !== false) {
      setSelectedIds(new Set());
    }
  };

  const totalRecordsCount = isServerPagination
    ? controlledTotalRecords ?? data?.length ?? 0
    : data?.length ?? 0;
  const { displayFrom, displayTo } = resolvePageSizeDisplayRange(
    currentPage,
    rowsPerPage,
    totalRecordsCount
  );

  const handleRowsPerPageChange = (num) => {
    persistPageSize(num);
    if (isServerPagination) {
      onPageSizeChange?.(num);
      return;
    }
    setRowsPerPage(num);
    setCurrentPage(1);
  };

  const handlePrevPage = () => {
    if (currentPage <= 1) return;
    const nextPage = currentPage - 1;
    scrollTablePaginationToTop();
    if (isServerPagination) {
      onPageChange?.(nextPage);
      return;
    }
    setCurrentPage(nextPage);
  };

  const handleNextPage = () => {
    if (currentPage >= totalPages) return;
    const nextPage = currentPage + 1;
    scrollTablePaginationToTop();
    if (isServerPagination) {
      onPageChange?.(nextPage);
      return;
    }
    setCurrentPage(nextPage);
  };

  const handlePageNumberClick = (page) => {
    if (!page || page === currentPage) return;
    scrollTablePaginationToTop();
    if (isServerPagination) {
      onPageChange?.(page);
      return;
    }
    setCurrentPage(page);
  };

  const paginationPageItems = useMemo(
    () => buildPaginationPageItems(currentPage, totalPages),
    [currentPage, totalPages]
  );

  // SVG Icons
  const IconColumns = (
    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
      <path d="M3 3h18v2H3V3zm0 8h18v2H3v-2zm0 8h18v2H3v-2z" />
    </svg>
  );
  const IconFilter = (
    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
      <path d="M10 18h4v-2h-4v2zm-7-8v2h18v-2H3zm3-6v2h12V4H6z" />
    </svg>
  );
  const IconList = (
    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
      <path d="M3 13h2v-2H3v2zm0 6h2v-2H3v2zm0-12h2V5H3v2zm4 0h14V5H7v2zm0 4h14v-2H7v2zm0 4h14v-2H7v2zm0 4h14v-2H7v2z" />
    </svg>
  );
  const IconDownload = (
    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
      <path d="M5 20h14v-2H5v2zM12 2v12l4-4h-3V2h-2v8H8l4 4z" />
    </svg>
  );
  const IconEdit = (
    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
      <path d="M3 17.25V21h3.75l11-11.03-3.75-3.75L3 17.25zm17.71-10.04a1.003 1.003 0 0 0 0-1.42l-2.5-2.5a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.99-1.66z" />
    </svg>
  );

  const IconDetails = (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 640 640"><path d="M320 128C241 128 175.3 185.3 162.3 260.7C171.6 257.7 181.6 256 192 256L208 256C234.5 256 256 277.5 256 304L256 400C256 426.5 234.5 448 208 448L192 448C139 448 96 405 96 352L96 288C96 164.3 196.3 64 320 64C443.7 64 544 164.3 544 288L544 456.1C544 522.4 490.2 576.1 423.9 576.1L336 576L304 576C277.5 576 256 554.5 256 528C256 501.5 277.5 480 304 480L336 480C362.5 480 384 501.5 384 528L384 528L424 528C463.8 528 496 495.8 496 456L496 435.1C481.9 443.3 465.5 447.9 448 447.9L432 447.9C405.5 447.9 384 426.4 384 399.9L384 303.9C384 277.4 405.5 255.9 432 255.9L448 255.9C458.4 255.9 468.3 257.5 477.7 260.6C464.7 185.3 399.1 127.9 320 127.9z" /></svg>
  );

  const IconView = (
    <svg xmlns="www.w3.org" width="14" height="14" fill="currentColor" className="bi bi-eye" viewBox="0 0 16 16">
      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
    </svg>

  );

  const SortIcon = ({ column }) => {
    const isActive = sortConfig.key === column;
    const direction = isActive ? sortConfig.direction : null;
    const activeColor = "#212529";
    const inactiveColor = "#c4c4c4";
    return (
      <span
        style={{
          display: "inline-flex",
          flexDirection: "column",
          lineHeight: 0,
          marginLeft: 4,
          verticalAlign: "middle",
        }}
      >
        <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: "block" }}>
          <path d="M4 0l4 5H0z" fill={direction === "asc" ? activeColor : inactiveColor} />
        </svg>
        <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: "block", marginTop: 2 }}>
          <path d="M4 5L0 0h8z" fill={direction === "desc" ? activeColor : inactiveColor} />
        </svg>
      </span>
    );
  };

  // Column modal
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [specialNotesModalOpen, setSpecialNotesModalOpen] = useState(false);
  const [specialNotesModalContext, setSpecialNotesModalContext] = useState({
    candidateName: "",
    specialNotes: "",
  });
  const isSpecialNotesNameClickPage = SPECIAL_NOTES_NAME_CLICK_PAGE_NAMES.has(pageNameNorm);

  const openSpecialNotesModal = (row = {}) => {
    setSpecialNotesModalContext({
      candidateName: String(row?.name ?? row?.candidateFullName ?? "").trim(),
      specialNotes: resolveRowSpecialNotes(row),
    });
    setSpecialNotesModalOpen(true);
  };

  const closeSpecialNotesModal = () => {
    setSpecialNotesModalOpen(false);
    setSpecialNotesModalContext({ candidateName: "", specialNotes: "" });
  };
  const toggleColumnModal = () => setShowColumnModal(!showColumnModal);

  const wideDateAccessors = new Set(["billDate", "regDate", "registerDate"]);
  const apiKeyAccessors = new Set(["apiKey", "api_key"]);

  /** My Call / Deactivate Candidate — column widths (metadata accessors are camelCase). */
  const getMyCallDeactivateColumnWidthStyle = (accessor) => {
    if (
      pageNameNorm !== "callexecutivemycall" &&
      pageNameNorm !== "callexecutivedeactivatecandidate"
    ) {
      return undefined;
    }
    const acc = String(accessor || "");
    const accLower = acc.toLowerCase();
    if (accLower.includes("phone")) {
      return { minWidth: "118px", width: "118px" };
    }
    const wideByAccessor = {
      clientName: { minWidth: "158px", width: "158px" },
      candidateName: { minWidth: "212px", width: "212px" },
      name: { minWidth: "212px", width: "212px" },
      jobName: { minWidth: "168px", width: "168px" },
      job: { minWidth: "168px", width: "168px" },
      createdAt: { minWidth: "172px", width: "172px" },
      createAt: { minWidth: "172px", width: "172px" },
      createdOn: { minWidth: "172px", width: "172px" },
      createdDate: { minWidth: "172px", width: "172px" },
      dateAdded: { minWidth: "172px", width: "172px" },
    };
    return wideByAccessor[accessor];
  };

  /** Client Report — wider phone column for formatted numbers. */
  const getClientReportColumnWidthStyle = (accessor) => {
    if (pageNameNorm !== "clientreport") return undefined;
    const accLower = String(accessor || "").toLowerCase();
    if (accLower.includes("phone")) {
      return { minWidth: "160px", width: "160px", whiteSpace: "nowrap" };
    }
    return undefined;
  };

  // Drag style function
  const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: "none",
    cursor: "grab",
    background: isDragging ? "#f1f5f9" : "transparent",
    boxShadow: isDragging ? "0 4px 8px rgba(0,0,0,0.15)" : "none",
    transition: "all 0.2s ease",
    ...draggableStyle,
  });

  const toSnakeCase = (value = "") =>
    String(value)
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .replace(/[\s-]+/g, "_")
      .toLowerCase();

  const toCamelCase = (value = "") =>
    String(value)
      .replace(/[-_\s]+(.)?/g, (_, char) => (char ? char.toUpperCase() : ""))
      .replace(/^(.)/, (m) => m.toLowerCase());

  const toPascalCase = (value = "") => {
    const camel = toCamelCase(value);
    return camel ? camel.charAt(0).toUpperCase() + camel.slice(1) : "";
  };

  const getItemValueByAccessor = (item, accessor) => {
    if (!item || !accessor) return "";
    if (item?.[accessor] !== undefined && item?.[accessor] !== null) return item[accessor];

    const normalizedAccessor = String(accessor).toLowerCase();
    const candidates = [
      accessor,
      toCamelCase(accessor),
      toPascalCase(accessor),
      toSnakeCase(accessor),
      String(accessor).toLowerCase(),
      String(accessor).toUpperCase(),
    ];

    for (const key of candidates) {
      if (item?.[key] !== undefined && item?.[key] !== null) return item[key];
    }

    const matchedKey = Object.keys(item).find(
      (key) => String(key).toLowerCase() === normalizedAccessor
    );
    if (matchedKey && item?.[matchedKey] !== undefined && item?.[matchedKey] !== null) {
      return item[matchedKey];
    }
    return "";
  };

  /** Same date/typing rules as legacy client table (`formatDateTimeValueForTable`). */
  const resolveColumnValue = (item, accessor) => {
    if (isCallExecutiveTable) {
      return resolveCallExecutiveExportCellValue(
        item,
        accessor,
        getItemValueByAccessor
      );
    }

    const value = getItemValueByAccessor(item, accessor);

    if (tableVariant === "client") {
      switch (accessor) {
        case "status":
          return value ?? "";
        case "quota":
        case "approved":
        case "candidateDeactive":
        case "forTools":
        case "isBackend":
        case "isbackend":
          return normalizeYesNoDisplayLabel(value) ?? "No";
        case "forClient":
        case "forJob":
          return normalizeYesNoDisplayLabel(value) ?? "No";
        case "incentive":
          return value ?? "";
        case "video":
        case "vLink":
        case "cvLink":
        case "mecImageLink":
          return value || "";
        case "is_allowed":
          return normalizeYesNoDisplayLabel(value) ?? "No";
        case "notes":
        case "fedex_id":
          return value || "";
        case "last_activation_date":
        case "lastActivationDate":
          return formatDateTimeValueForTable(value, accessor);
        default:
          if (isQuotaStyleYesNoAccessor(accessor)) {
            return normalizeYesNoDisplayLabel(value) ?? "No";
          }
          if (isDownloadLinkColumnAccessor(accessor)) {
            return value || "";
          }
          return formatDateTimeValueForTable(value, accessor);
      }
    }

    if (tableVariant === "common") {
      switch (accessor) {
        case "status":
          return value ?? "";
        case "quota":
          return value ? "Yes" : "No";
        case "candidateDeactive":
        case "forTools":
        case "isForUSCitizens":
        case "isForNonUSCitizens":
        case "isDefault":
          return resolveYesNoBadgeLabel(value);
        case "forClient":
        case "forJob":
          return resolveYesNoBadgeLabel(value);
        case "incentive":
          return value ?? "";
        case "video":
        case "vLink":
        case "cvLink":
        case "mecImageLink":
          return value || "";
        case "is_allowed":
          return value ? "Yes" : "No";
        case "notes":
        case "fedex_id":
          return value || "";
        default:
          if (isDownloadLinkColumnAccessor(accessor)) {
            return value || "";
          }
          return formatDateTimeValueForTable(value, accessor);
      }
    }

    switch (accessor) {
      case "status":
        return value ?? "";
      case "quota":
      case "approved":
        return value ? "Yes" : "No";
      case "candidateDeactive":
      case "forTools":
        return value ? "Yes" : "No";
      case "forClient":
      case "forJob":
        return value == "1" ? "Yes" : "No";
      case "incentive":
        return value ?? "";
      case "video":
      case "vLink":
      case "cvLink":
      case "mecImageLink":
        return value || "";
      case "is_allowed":
        return value ? "Yes" : "No";
      case "notes":
      case "fedex_id":
        return value || "";
      case "last_activation_date":
      case "lastActivationDate":
        return formatDateTimeValueForTable(value, accessor);
      default:
        if (isDownloadLinkColumnAccessor(accessor)) {
          return value || "";
        }
        return formatDateTimeValueForTable(value, accessor);
    }
  };



  const getVisibleColumns = () =>
    visibleColumnOrder
      .map((preferenceKey) =>
        findTableColumnDefinitionByPreferenceKey(columns, preferenceKey)
      )
      .filter(Boolean);

  const visibleColumnCount = getVisibleColumns()?.length || 0;
  const noDataColSpan =
    visibleColumnCount +
    (isCheckboxLeftSide ? 1 : 0) +
    (isActionRequired ? 1 : 0);

  // const getTableData = () =>
  //   sortedData.map((row) =>
  //     getVisibleColumns().map((col) => row[col.accessor] ?? "")
  //   );

  const getTableData = () =>
    sortedData.map((row) =>
      getVisibleColumns().map((col) =>
        resolveColumnValue(row, col.accessor)
      )
    );


  const handleCopy = async () => {
    const headers = getVisibleColumns().map(getColumnExportHeaderLabel).join("\t");
    const rows = getTableData()
      .map((r) => r.join("\t"))
      .join("\n");

    await navigator.clipboard.writeText(`${headers}\n${rows}`);
  };
  const handleCSV = () => {
    const headers = getVisibleColumns().map(getColumnExportHeaderLabel);
    const rows = getTableData();

    const csvContent = [headers, ...rows]
      .map((e) => e.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${pageName}  - (DEV) Ancile Hiring Tools.csv`;
    link.click();
  };
  // const handleExcel = () => {
  //   const wsData = [
  //     getVisibleColumns().map((c) => c.Header),
  //     ...getTableData(),
  //   ];

  //   const worksheet = XLSX.utils.aoa_to_sheet(wsData);
  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

  //   XLSX.writeFile(workbook, "table.xlsx");
  // };

  const handleExcel = () => {
    const headers = getVisibleColumns().map(getColumnExportHeaderLabel);
    const rows = getTableData().map((row) =>
      row.map((value) => (value == null ? "" : String(value)))
    );

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    worksheet["!cols"] = headers.map((headerLabel) => {
      const normalizedHeader = String(headerLabel).toLowerCase();
      if (normalizedHeader === "id") return { wch: 6 };
      if (normalizedHeader === "video") return { wch: 80 };
      return { wch: 30 };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, "Data");

    XLSX.writeFile(wb, `${pageName}  - (DEV) Ancile Hiring Tools.xlsx`);
  };



  const handlePrint = () => {
    const headers = getVisibleColumns()
      .map((c) => `<th>${getColumnExportHeaderLabel(c)}</th>`)
      .join("");
    const rows = getTableData()
      .map(
        (r) =>
          `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`
      )
      .join("");

    const win = window.open("", "", "width=900,height=700");
    win.document.write(`
        <html>
          <head>
            <title>Print</title>
            <style>
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            </style>
          </head>
          <body>
            <table>
              <thead><tr>${headers}</tr></thead>
              <tbody>${rows}</tbody>
            </table>
          </body>
        </html>
      `);
    win.document.close();
    win.print();
  };
  const handlePDF = () => {
    const doc = new jsPDF("l", "pt", "a4"); // landscape like screenshot

    const columns = getVisibleColumns().map(getColumnExportHeaderLabel);
    const rows = getTableData();

    autoTable(doc, {
      head: [columns],
      body: rows,

      startY: 40,
      theme: "striped",

      styles: {
        fontSize: 9,
        cellPadding: { top: 6, right: 6, bottom: 6, left: 6 },
        textColor: "#000",
        lineColor: "#e5e7eb",
        lineWidth: 0.5,
        valign: "middle",
        halign: "left",
      },

      headStyles: {
        fillColor: "#2f3e4e",   // dark blue header
        textColor: "#ffffff",
        fontStyle: "bold",
        fontSize: 10,
        halign: "left",
        valign: "middle",
      },

      alternateRowStyles: {
        fillColor: "#f3f4f6", // light gray stripe
      },

      bodyStyles: {
        fillColor: "#ffffff",
      },

      didParseCell: function (data) {
        // Prevent long text overflow like emails
        data.cell.styles.cellWidth = "wrap";
      },

      margin: { left: 20, right: 20 },
    });

    doc.save(`${pageName}  - (DEV) Ancile Hiring Tools.pdf`);
  };

  const handleLinkDownload = async (url) => {
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const getDownloadUrl = (value) => {
    if (!value) return "";
    if (/^https?:\/\//i.test(value)) return value;
    return `${process.env.REACT_APP_LINK_URL || ""}${value}`;
  };

  const renderApiKeyCell = (item, col, idx, cellValue) => {
    const rowKey = item?.id || item?._id || idx;
    const visibilityKey = `${rowKey}-${col?.accessor}`;
    const isVisible = !!visibleApiKeyMap?.[visibilityKey];
    const hasValue = !!String(cellValue || "").trim();
    return (
      <div className="d-flex align-items-center gap-2">
        <span>{hasValue ? (isVisible ? cellValue : "XXXXXXXXXXXXXXXXXXXX") : ""}</span>
        {hasValue ? (
          <span
            role="button"
            className="text-muted cursor-pointer team-table-api-key-toggle"
            style={{ marginLeft: "32px" }}
            onClick={() => toggleApiKeyVisibility(rowKey, col?.accessor)}
            title={isVisible ? "Hide API key" : "Show API key"}
          >
            <i
              className={`mdi ms-1 ${isVisible ? "mdi-eye-off-outline" : "mdi-eye-outline"} font-size-18`}
            />
          </span>
        ) : null}
      </div>
    );
  };

  const renderLinkDownloadCell = (cellValue) =>
    cellValue ? (
      <Button
        color="link"
        className="p-0 text-decoration-none"
        onClick={() => handleLinkDownload(getDownloadUrl(cellValue))}
        title="Download"
      >
        <i className="fa fa-download" aria-hidden="true" />
      </Button>
    ) : (
      <span>-</span>
    );

  const renderPhoneTableCell = (cellValue, accessor, variant) => {
    const formattedPhone = phoneNumberShowFormat(cellValue);
    if (!formattedPhone) {
      return <span className="text-muted">—</span>;
    }
    return (
      <span className={mergeDataCellClasses(accessor, variant)}>
        {formattedPhone}
      </span>
    );
  };

  const renderTableBodyCellContent = (col, item, idx) => {
    const cellValue = resolveColumnValue(item, col?.accessor);
    const rawValue = getItemValueByAccessor(item, col?.accessor);

    if (isGrandTotalSummaryRow(item)) {
      const norm = normalizeTableBodyAccessor(col?.accessor);
      if (norm === "serialnumber" || norm === "sn") return "";
      if (norm === "callstage" || norm === "stage") return "Grand Total";
      if (norm === "totalcalltrigger" || norm === "grandtotal") {
        return item?.totalCallTrigger ?? item?.grandTotal ?? "";
      }
      return "";
    }

    if (isIdTableColumnAccessor(col?.accessor)) {
      return (
        <MaskedIdTableCell
          maskedId={cellValue}
          className={mergeDataCellClasses(col?.accessor, tableVariant)}
        />
      );
    }

    if (isCallExecutiveTable) {
      return renderCallExecutiveTableBodyCell({
        columnDefinition: col,
        rowRecord: item,
        rowIndex: idx,
        onQuickUpdateBlur: onCallExecutiveQuickUpdateBlur,
        getItemValueByAccessor,
        pageName,
      });
    }

    if (tableVariant === "common") {
      if (col?.accessor === "incentive") {
        return (
          <span className="px-2 py-1 fw-semibold incentive-stl">
            {cellValue}
          </span>
        );
      }
      const rowStatusLabelBadge = renderRowStatusLabelMetadataBadge(
        item,
        col?.accessor,
        pageName
      );
      if (rowStatusLabelBadge) return rowStatusLabelBadge;
      const readyToSendStatusBadge = renderReadyToSendStatusMetadataBadge(
        item,
        col?.accessor,
        pageName
      );
      if (readyToSendStatusBadge) return readyToSendStatusBadge;
      if (isCommonTableStatusMetadataColumn(col, pageNameNorm)) {
        const statusCellValue = resolveStatusTableCellValue(item, col?.accessor);
        return (
          <StatusMetadataBadge
            accessor={
              String(col?.accessor ?? "")
                .trim()
                .toLowerCase()
                .replace(/_/g, "") === "statustitle"
                ? "status"
                : col?.accessor
            }
            pageName={pageName}
            value={normalizeCandidateStatusMetadataValue(
              col?.accessor,
              statusCellValue
            )}
            fallbackClassName={statusType(String(statusCellValue ?? ""))}
          />
        );
      }
      if (isYesNoBadgeTableColumnAccessor(col?.accessor)) {
        return (
          <StatusMetadataBadge
            accessor={col?.accessor}
            pageName={pageName}
            value={resolveYesNoBadgeLabel(rawValue)}
            fallbackClassName={quotaType(resolveYesNoBadgeLabel(rawValue))}
          />
        );
      }
      if (isDownloadLinkColumnAccessor(col?.accessor)) {
        return renderLinkDownloadCell(cellValue);
      }
      if (isSpecialNotesNameClickPage && col?.accessor === "name") {
        const hasSpecialNote = rowHasSpecialNote(item);
        const nameLabel = cellValue || "—";
        return (
          <div>
            <button
              type="button"
              className={`btn btn-link p-0 text-start text-decoration-none common-table-name-email-cell fw-semibold ${
                hasSpecialNote ? "text-primary" : ""
              }`}
              onClick={() => openSpecialNotesModal(item)}
            >
              {nameLabel}
            </button>
            {hasSpecialNote ? (
              <span className="badge bg-primary mt-1">SpecialNote</span>
            ) : null}
          </div>
        );
      }
      if (NOTES_TEXTAREA_ACCESSORS.includes(col?.accessor)) {
        return renderNotesTextareaCell(cellValue, item, col, onNotesBlur);
      }
      const isAllowedCheckbox = renderIsAllowedCheckbox(item, col, cellValue, onToggleAllowed);
      if (isAllowedCheckbox) return isAllowedCheckbox;
      if (isPhoneColumnAccessor(col?.accessor)) {
        return renderPhoneTableCell(cellValue, col?.accessor, tableVariant);
      }
      return (
        <span className={mergeDataCellClasses(col?.accessor, tableVariant)}>
          {formatDateTimeValueForTable(cellValue, col?.accessor)}
        </span>
      );
    }

    if (tableVariant === "client") {
      if (STATUS_METADATA_BADGE_ACCESSORS.includes(col?.accessor)) {
        return (
          <StatusMetadataBadge
            accessor={col?.accessor}
            pageName={pageName}
            value={cellValue}
            fallbackClassName={
              col?.accessor === "approved" ? quotaType(cellValue) : incentiveType(cellValue)
            }
          />
        );
      }
      if (isClientTableStatusColumn(col)) {
        return (
          <StatusMetadataBadge
            accessor={
              STATUS_STYLE_ACCESSORS.includes(col?.accessor) ? col?.accessor : "status"
            }
            pageName={pageName}
            value={cellValue}
            fallbackClassName={statusType(cellValue)}
          />
        );
      }
      if (ONBOARDING_STYLE_ACCESSORS.includes(col?.accessor)) {
        return (
          <span className={`${TABLE_STATUS_BADGE_CLASS} ${cellValue ? onBoardingStatusType(cellValue) : ""}`}>
            {cellValue}
          </span>
        );
      }
      if (isQuotaStyleYesNoAccessor(col?.accessor)) {
        return (
          <StatusMetadataBadge
            accessor={col?.accessor}
            pageName={pageName}
            value={cellValue}
            fallbackClassName={quotaType(cellValue)}
          />
        );
      }
      if (FOR_CLIENT_JOB_ACCESSORS.includes(col?.accessor)) {
        return (
          <span className={`${TABLE_STATUS_BADGE_CLASS} ${quotaType(cellValue)}`}>
            {cellValue}
          </span>
        );
      }
      if (col?.accessor === "link" || isDownloadLinkColumnAccessor(col?.accessor)) {
        return renderLinkDownloadCell(cellValue);
      }
      if (NOTES_TEXTAREA_ACCESSORS.includes(col?.accessor)) {
        return renderNotesTextareaCell(cellValue, item, col, onNotesBlur);
      }
      if (["apikey", "api_key"].includes(String(col?.accessor || "").toLowerCase())) {
        return renderApiKeyCell(item, col, idx, cellValue);
      }
      const isAllowedCheckbox = renderIsAllowedCheckbox(item, col, cellValue, onToggleAllowed);
      if (isAllowedCheckbox) return isAllowedCheckbox;
      if (isPhoneColumnAccessor(col?.accessor)) {
        return renderPhoneTableCell(cellValue, col?.accessor, tableVariant);
      }
      return (
        <span className={mergeDataCellClasses(col?.accessor, tableVariant)}>
          {formatDateTimeValueForTable(cellValue, col?.accessor)}
        </span>
      );
    }

    if (col?.accessor === "incentive") {
      return (
        <span className={`px-2 py-1 fw-semibold ${incentiveType(cellValue)}`}>
          {cellValue}
        </span>
      );
    }
    if (TEAM_STATUS_BADGE_ACCESSORS.includes(col?.accessor)) {
      return (
        <StatusMetadataBadge
          accessor={col?.accessor}
          pageName={pageName}
          value={cellValue}
          fallbackClassName={statusType(cellValue)}
        />
      );
    }
    if (ONBOARDING_STYLE_ACCESSORS.includes(col?.accessor)) {
      return (
        <span className={`${TABLE_STATUS_BADGE_CLASS} ${cellValue ? onBoardingStatusType(cellValue) : ""}`}>
          {cellValue}
        </span>
      );
    }
    if (TEAM_QUOTA_STYLE_BOOLEAN_ACCESSORS.includes(col?.accessor)) {
      return (
        <span className={`${TABLE_STATUS_BADGE_CLASS} ${quotaType(cellValue ? "Yes" : "No")}`}>
          {cellValue ? "Yes" : "No"}
        </span>
      );
    }
    if (TEAM_FOR_CLIENT_JOB_ACCESSORS.includes(col?.accessor)) {
      return (
        <span className={`${TABLE_STATUS_BADGE_CLASS} ${quotaType(cellValue == "1" ? "Yes" : "No")}`}>
          {cellValue == "1" ? "Yes" : "No"}
        </span>
      );
    }
    if (col?.accessor === "link" || isDownloadLinkColumnAccessor(col?.accessor)) {
      return renderLinkDownloadCell(cellValue);
    }
    if (col?.accessor === "notes" || col?.accessor === "fedex_id") {
      return renderNotesTextareaCell(cellValue, item, col, onNotesBlur);
    }
    if (["apikey", "api_key"].includes(String(col?.accessor || "").toLowerCase())) {
      return renderApiKeyCell(item, col, idx, cellValue);
    }
    const isAllowedCheckbox = renderIsAllowedCheckbox(item, col, cellValue, onToggleAllowed);
    if (isAllowedCheckbox) return isAllowedCheckbox;
    if (isPhoneColumnAccessor(col?.accessor)) {
      return renderPhoneTableCell(cellValue, col?.accessor, tableVariant);
    }
    return (
      <span className={mergeDataCellClasses(col?.accessor, tableVariant)}>
        {formatDateTimeValueForTable(cellValue, col?.accessor)}
      </span>
    );
  };

  const renderTableEditAction = ({
    rowKey,
    idPrefix,
    href,
    onClick,
    iconClass = "mdi mdi-pencil text-success",
  }) => {
    const actionId = `${idPrefix}-edit-${rowKey}`;
    const buttonClass =
      "team-table-row-action-btn d-inline-flex align-items-center justify-content-center";

    return (
      <>
        {href ? (
          <Link to={href} className={buttonClass} id={actionId} aria-label="Edit">
            <i className={iconClass} aria-hidden />
          </Link>
        ) : (
          <span
            className={buttonClass}
            onClick={onClick}
            role="button"
            id={actionId}
          >
            <i className={iconClass} aria-hidden />
          </span>
        )}
        <UncontrolledTooltip placement="top" target={actionId}>
          Edit
        </UncontrolledTooltip>
      </>
    );
  };

  const renderClientRegisterRowAction = (rowKey, { actionKey, tooltip, iconClass, extraClass = "", onClick, idPrefix = "client-register" }) => {
    const actionId = `${idPrefix}-${actionKey}-${rowKey}`;
    return (
      <div key={actionKey} className="d-inline-flex align-items-center justify-content-center">
        <span
          className={`team-table-row-action-btn d-inline-flex align-items-center justify-content-center ${extraClass}`.trim()}
          onClick={onClick}
          role="button"
          id={actionId}
        >
          <i className={iconClass} aria-hidden />
        </span>
        <UncontrolledTooltip placement="top" target={actionId}>
          {tooltip}
        </UncontrolledTooltip>
      </div>
    );
  };

  const paginationSizeOptions = buildPageSizeOptions(
    isServerPagination || tableVariant !== "common" ? DEFAULT_PAGE_SIZE_OPTIONS : [5, 10, 20, 50, 100],
    canShowAllRows
  );

  const actionColumnMinWidth =
    pageNameNorm === "clientregister"
      ? "158px"
      : pageNameNorm === "jobdescription"
        ? "132px"
      : pageNameNorm === "manageuser" || pageNameNorm === "companyuser"
        ? typeof onResetBlock === "function"
          ? "132px"
          : "96px"
      : pageNameNorm === "callmanagerorderinprogress"
        ? "110px"
        : "88px";

  return (
    <Card className="shadow-sm border-0">
      <CardBody className="p-0">
        {/* Toolbar */}
        <div className="d-flex align-items-center justify-content-between px-2 py-2 pad-btm" style={{ backgroundColor: "#f8fafc" }}>
          <div className="d-flex align-items-center gap-2">
            <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline table-action-icon" onClick={toggleColumnModal}>
              <i className="fa fa-columns" aria-hidden="true"></i>
              COLUMNS
            </Button>
            {/* <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline" onClick={()=>setCommonFilterModalOpen(true)}>
            <i className="fa fa-filter"></i> Filters
            </Button> */}
            {/* <Button color="link" className="text-dark d-flex align-items-center gap-2 p-0 fw-semibold">
              {IconFilter} Filters
            </Button>
            
            <Button color="link" className="text-dark d-flex align-items-center gap-2 p-0 fw-semibold">
              {IconList} Density
            </Button> */}
            {/* <Button color="link" className="text-dark d-flex align-items-center gap-2 p-0 fw-semibold">
              {IconDownload} Export
            </Button> */}
            {showCopyCsvPrintToolbar ? (
              <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-csv" onClick={handleCopy}>
                <i className="fa fa-copy" aria-hidden="true"></i>
                COPY
              </Button>
            ) : null}
            {/* <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-csv" onClick={handleCSV}>
              <i className="fas fa-file-csv"></i>
              CSV
            </Button> */}
            <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-excel" onClick={handleExcel}>
              <i className="fas fa-file-excel"></i>
              EXCEL
            </Button>
            {/* <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-print" onClick={handlePrint}>
              <i className="fa fa-print" aria-hidden="true"></i>
              PRINT
            </Button> */}
            <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-pdf" onClick={handlePDF}>
              <i className="fa fa-file-pdf" aria-hidden="true"></i>
              PDF
            </Button>
          </div>
          {allCommonFilter ? (
            <TableFilterToolbarActions
              activeFilterCount={activeFilterCount}
              onOpenFilters={() => setCommonFilterModalOpen(true)}
              onResetFilters={() => setCommonResetFilter(true)}
            />
          ) : null}

          {/* <Button color="primary" size="sm" onClick={toggleColumnModal}>
            Manage Columns
          </Button> */}
        </div>
        {enableRowSelection && isCheckboxLeftSide && typeof onBulkAction === "function" ? (
          <div className="d-flex justify-content-end align-items-center px-2 py-2 border-bottom candidate-bulk-bar-bg">
            <Button
              color="primary"
              size="sm"
              className="border-radius-zero"
              type="button"
              disabled={!selectedIds.size}
              onClick={handleBulkActionClick}
            >
              {bulkActionLabel}
            </Button>
          </div>
        ) : null}
        {/* <hr /> */}



        {/* Table */}
        <div
          ref={tableScrollRef}
          className={[
            "table-responsive mb-4",
            isScrolledToEnd ? "table-scroll-at-end" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{ backgroundColor: "#f8fafc" }}
        >
          <Table hover borderless className="align-middle mb-0 custom-table">
            <DragDropContext onDragEnd={handleColumnDragEnd}>
              <Droppable droppableId="columns" direction="horizontal">
                {(provided) => (
                  <thead ref={provided.innerRef} {...provided.droppableProps} style={{ backgroundColor: "#f8fafc" }}>
                    <tr style={{ color: "#374151", fontWeight: 600, fontSize: "14px" }}>
                      {isCheckboxLeftSide &&

                        <th style={{ width: "40px" }}>
                          {enableRowSelection ? (
                            <input
                              key={`bulk-hdr-${controlledCurrentPage}-${controlledRowsPerPage}-${allPageSelected}-${somePageSelected}`}
                              ref={headerCheckboxRef}
                              type="checkbox"
                              className="form-check-input"
                              defaultChecked={allPageSelected}
                              onChange={toggleSelectAllOnPage}
                              aria-label="Select all on this page"
                            />
                          ) : null}
                        </th>
                      }

                      {visibleColumnOrder?.map((columnPreferenceKey, index) => {
                        const col = findTableColumnDefinitionByPreferenceKey(
                          columns,
                          columnPreferenceKey
                        );
                        if (!col?.accessor) return null;
                        const draggableId =
                          getTableColumnPreferenceKey(col) || columnPreferenceKey;

                        return (
                          <Draggable key={draggableId} draggableId={draggableId} index={index}>
                            {(provided, snapshot) => (
                              <th
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                onClick={() => handleSort(col?.accessor)}
                                className={getTableColumnHeaderClassName(col, pageName, tableVariant)}
                                style={{
                                  ...getItemStyle(snapshot.isDragging, provided.draggableProps.style),
                                  ...(wideDateAccessors.has(col?.accessor)
                                    ? { minWidth: "140px", width: "140px" }
                                    : {}),
                                  ...(apiKeyAccessors.has(col?.accessor)
                                    ? { minWidth: "260px", width: "260px", maxWidth: "260px" }
                                    : {}),
                                  ...(getMyCallDeactivateColumnWidthStyle(col?.accessor) || {}),
                                  ...(getClientReportColumnWidthStyle(col?.accessor) || {}),
                                  ...(getToggleCheckboxColumnWidthStyle(col?.accessor) || {}),
                                }}
                              >
                                {col?.Header} <SortIcon column={col?.accessor} />
                              </th>
                            )}
                          </Draggable>
                        );
                      })}

                      {provided.placeholder}
                      {isActionRequired && (
                        <th
                          className={[
                            "align-middle text-center team-table-actions-cell",
                            isClientStyleTable ? "text-nowrap" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          style={{
                            width: actionColumnMinWidth,
                            minWidth: actionColumnMinWidth,
                            maxWidth: actionColumnMinWidth,
                          }}
                        >
                          Action
                        </th>
                      )}
                    </tr>
                  </thead>
                )}
              </Droppable>

              <tbody style={{ backgroundColor: "#ffffff" }}>
                {!paginatedData?.length ? (
                  <tr>
                    <td
                      colSpan={Math.max(noDataColSpan, 1)}
                      className="text-center text-muted"
                      style={{ padding: "0", verticalAlign: "middle" }}
                    >
                      <div
                        style={{
                          minHeight: "180px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          fontSize: "14px",
                        }}
                      >
                        No data found
                      </div>
                    </td>
                  </tr>
                ) : paginatedData?.map((item, idx) => {
                  const rowId = getRowId(item);
                  const isRowSelected = enableRowSelection && selectedIds.has(rowId);
                  return (
                  <tr
                    key={rowId || idx}
                    className={isGrandTotalSummaryRow(item) ? "call-scheduled-report-grand-total-row" : undefined}
                  >
                    {isCheckboxLeftSide &&
                      <td>
                        {enableRowSelection ? (
                          <input
                            key={`row-cb-${rowId}-${isRowSelected}`}
                            type="checkbox"
                            className="form-check-input"
                            defaultChecked={isRowSelected}
                            onChange={() => toggleRowId(rowId)}
                            aria-label={`Select row ${rowId}`}
                          />
                        ) : null}
                      </td>
                    }
                    {visibleColumnOrder?.map((columnPreferenceKey) => {
                      const col = findTableColumnDefinitionByPreferenceKey(
                        columns,
                        columnPreferenceKey
                      );
                      if (!col?.accessor) return null;

                      return (
                        <td
                          key={columnPreferenceKey}
                          className={getTableColumnCellClassName(col, pageName, tableVariant, item)}
                          style={
                            getMyCallDeactivateColumnWidthStyle(col?.accessor) ||
                            getClientReportColumnWidthStyle(col?.accessor) ||
                            getToggleCheckboxColumnWidthStyle(col?.accessor) ||
                            (wideDateAccessors.has(col?.accessor)
                              ? { minWidth: "140px", width: "140px" }
                              : apiKeyAccessors.has(col?.accessor)
                                ? { minWidth: "170px", width: "170px", maxWidth: "170px" }
                                : undefined)
                          }
                        >
                          {renderTableBodyCellContent(col, item, idx)}
                        </td>
                      );
                    })}
                    {isActionRequired ? (
                      <td
                        className="align-middle text-center team-table-actions-cell px-1 py-1"
                        style={{
                          width: actionColumnMinWidth,
                          minWidth: actionColumnMinWidth,
                          maxWidth: actionColumnMinWidth,
                        }}
                      >
                        <>
                          {isClientStyleTable && (
                            <>
                              {(["company", "team", "usertype", "workshift", "warningletter", "monthylist", "teamrecognitionmonthylist", "mailtemplate"].includes(pageNameNorm)) && (
                                <div className="d-inline-flex align-items-center justify-content-center gap-1 flex-wrap">
                                  {(pageNameNorm !== "company" ||
                                    typeof onEdit === "function") && (
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                      onClick={() => onEdit?.(item)}
                                      role="button"
                                      id={`${pageNameNorm}-edit-${item?.id || item?._id || idx}`}
                                    >
                                      <i className="mdi mdi-pencil text-success" aria-hidden />
                                    </span>
                                  )}
                                  {pageNameNorm === "company" &&
                                    typeof onView === "function" && (
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center text-info"
                                      onClick={() => onView?.(item)}
                                      role="button"
                                    >
                                      <i className="mdi mdi-account text-success" aria-hidden />
                                    </span>
                                  )}
                                </div>
                              )}
                              {(["client", "clientsource", "pre-questioning", "onboarding", "clientreview", "clientappreciation", "offboarding"].includes(pageNameNorm)) && (
                                <div className="d-inline-flex align-items-center justify-content-center gap-1">
                                  {typeof onEdit === "function" || typeof getEditHref === "function" ? (
                                    renderTableEditAction({
                                      rowKey: item?.id || item?._id || idx,
                                      idPrefix: pageNameNorm,
                                      href:
                                        typeof getEditHref === "function"
                                          ? getEditHref(item)
                                          : null,
                                      onClick: () => onEdit?.(item),
                                    })
                                  ) : null}
                                  {typeof onView === "function" ? (
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                      onClick={() => onView?.(item)}
                                      role="button"
                                      id={`${pageNameNorm}-view-${item?.id || item?._id || idx}`}
                                    >
                                      <i
                                        className={
                                          pageNameNorm === "client"
                                            ? "mdi mdi-account text-success"
                                            : "mdi mdi-eye-outline text-primary"
                                        }
                                        aria-hidden
                                      />
                                    </span>
                                  ) : null}
                                  {typeof onDelete === "function" &&
                                    (pageNameNorm === "offboarding" && !item?.canDelete ? (
                                      <span className="text-muted">-</span>
                                    ) : (
                                      (pageNameNorm !== "offboarding" || item?.canDelete) && (
                                        <span
                                          className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                          onClick={() => onDelete?.(item)}
                                          role="button"
                                          id={`${pageNameNorm}-delete-${item?.id || item?._id || idx}`}
                                        >
                                          <i className="mdi mdi-delete text-danger" aria-hidden />
                                        </span>
                                      )
                                    ))}
                                  {typeof onCopy === "function" && (
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                      onClick={() => onCopy?.(item)}
                                      role="button"
                                      id={`${pageNameNorm}-copy-${item?.id || item?._id || idx}`}
                                    >
                                      <i className="mdi mdi-content-copy text-primary" aria-hidden />
                                    </span>
                                  )}
                                </div>
                              )}
                              {pageNameNorm === "jobdescription" && (() => {
                                const rowKey = item?.id || item?._id || idx;
                                return (
                                  <div className="d-inline-flex align-items-center justify-content-center gap-1">
                                    {typeof onEdit === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "edit",
                                          tooltip: "Edit",
                                          iconClass: "mdi mdi-pencil text-success",
                                          idPrefix: "jobdescription",
                                          onClick: () => onEdit(item),
                                        })
                                      : null}
                                    {typeof onDuplicate === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "duplicate",
                                          tooltip: "Duplicate",
                                          iconClass: "mdi mdi-content-duplicate text-info",
                                          idPrefix: "jobdescription",
                                          onClick: () => onDuplicate(item),
                                        })
                                      : null}
                                    {typeof onDelete === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "delete",
                                          tooltip: "Delete",
                                          iconClass: "mdi mdi-delete text-danger",
                                          idPrefix: "jobdescription",
                                          onClick: () => onDelete(item),
                                        })
                                      : null}
                                    {typeof onCopy === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "copy",
                                          tooltip: "Copy",
                                          iconClass: "mdi mdi-content-copy text-primary",
                                          idPrefix: "jobdescription",
                                          onClick: () => onCopy(item),
                                        })
                                      : null}
                                  </div>
                                );
                              })()}
                              {pageNameNorm === "clientregister" && (() => {
                                const rowKey = item?.id || item?._id || idx;
                                return (
                                  <div className="d-inline-flex flex-wrap align-items-center justify-content-center client-register-action-grid">
                                    {typeof onEdit === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "edit",
                                          tooltip: "Edit",
                                          iconClass: "mdi mdi-pencil text-success",
                                          onClick: () => onEdit(item),
                                        })
                                      : null}
                                    {typeof onCopy === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "copy",
                                          tooltip: "Copy",
                                          iconClass: "mdi mdi-content-copy text-primary",
                                          onClick: () => onCopy(item),
                                        })
                                      : null}
                                    {typeof onView === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "view",
                                          tooltip: "View",
                                          iconClass: "mdi mdi-eye-outline text-primary",
                                          onClick: () => onView(item),
                                        })
                                      : null}
                                    {typeof onBill === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "bill",
                                          tooltip: "Bill",
                                          iconClass:
                                            "mdi mdi-receipt text-secondary",
                                          onClick: () => onBill(item),
                                        })
                                      : null}
                                    {typeof onPermalink === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "permalink",
                                          tooltip: "Permalink",
                                          iconClass: "mdi mdi-link-variant text-info",
                                          onClick: () => onPermalink(item),
                                        })
                                      : null}
                                    {typeof onOffboarding === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "offboarding",
                                          tooltip: "Offboarding",
                                          iconClass: "mdi mdi-account-off text-muted",
                                          onClick: () => onOffboarding(item),
                                        })
                                      : null}
                                    {typeof onIncentiveTarget === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "incentive",
                                          tooltip: "Incentive Target",
                                          iconClass: "mdi mdi-cash-multiple text-warning",
                                          onClick: () => onIncentiveTarget(item),
                                        })
                                      : null}
                                    {typeof onJobDescription === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "job-description",
                                          tooltip: "Job Description",
                                          iconClass:
                                            "mdi mdi-file-document-edit-outline text-dark",
                                          onClick: () => onJobDescription(item),
                                        })
                                      : null}
                                    {typeof onAppreciation === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "appreciation",
                                          tooltip: "Appreciation",
                                          iconClass: "mdi mdi-hand-heart-outline text-danger",
                                          onClick: () => onAppreciation(item),
                                        })
                                      : null}
                                    {typeof onPreQuestioning === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "pre-questioning",
                                          tooltip: "Pre Questioning",
                                          iconClass: "mdi mdi-comment-question-outline",
                                          extraClass: "client-register-action-icon-preq",
                                          onClick: () => onPreQuestioning(item),
                                        })
                                      : null}
                                    {typeof onAdvantageLogin === "function"
                                      ? renderClientRegisterRowAction(rowKey, {
                                          actionKey: "advantage-login",
                                          tooltip: "Advantage Login",
                                          iconClass: "mdi mdi-login text-primary",
                                          onClick: () => onAdvantageLogin(item),
                                        })
                                      : null}
                                  </div>
                                );
                              })()}
                            </>
                          )}
                          {!isClientStyleTable && isCompanyUserTable && (() => {
                            const rowActions = item?.actions ?? {};
                            const showEdit =
                              typeof onEdit === "function" && rowActions.canEdit !== false;
                            const showDelete =
                              typeof onDelete === "function" && rowActions.canRemove !== false;
                            const showResetBlock =
                              typeof onResetBlock === "function" &&
                              rowActions.canResetBlock === true &&
                              item?.blocked === true;
                            return (
                              <div className="d-inline-flex align-items-center justify-content-center gap-1 flex-wrap">
                                {showEdit ? (
                                  <>
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                      onClick={() => onEdit(item)}
                                      id={`company-user-edit-${item?.id || item?._id || idx}`}
                                    >
                                      <i className="mdi mdi-pencil font-size-18 text-success" aria-hidden="true" />
                                    </span>
                                    <UncontrolledTooltip target={`company-user-edit-${item?.id || item?._id || idx}`} placement="top">
                                      Edit
                                    </UncontrolledTooltip>
                                  </>
                                ) : null}
                                {showDelete ? (
                                  <>
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                      onClick={() => onDelete(item)}
                                      id={`company-user-delete-${item?.id || item?._id || idx}`}
                                    >
                                      <i className="mdi mdi-delete font-size-18 text-danger" aria-hidden="true" />
                                    </span>
                                    <UncontrolledTooltip target={`company-user-delete-${item?.id || item?._id || idx}`} placement="top">
                                      Delete
                                    </UncontrolledTooltip>
                                  </>
                                ) : null}
                                {showResetBlock ? (
                                  <>
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                      onClick={() => onResetBlock(item)}
                                      id={`company-user-reset-block-${item?.id || item?._id || idx}`}
                                      role="button"
                                    >
                                      <i className="mdi mdi-lock-reset font-size-18 text-danger" aria-hidden="true" />
                                    </span>
                                    <UncontrolledTooltip
                                      target={`company-user-reset-block-${item?.id || item?._id || idx}`}
                                      placement="top"
                                    >
                                      Reset block
                                    </UncontrolledTooltip>
                                  </>
                                ) : null}
                              </div>
                            );
                          })()}
                          {!isClientStyleTable && isManageUserTable && (() => {
                            const rowActions = item?.actions ?? {};
                            const showEdit =
                              typeof onEdit === "function" && rowActions.canEdit === true;
                            const showClientAccess =
                              typeof onClientAccess === "function" &&
                              rowActions.canClientAccess === true;
                            const showTemporaryClientAccess =
                              typeof onTemporaryClientAccess === "function" &&
                              rowActions.canTempClientAccess === true;
                            const showPermission =
                              typeof onPermission === "function" &&
                              rowActions.canPermission === true;
                            const showResetBlock =
                              typeof onResetBlock === "function" &&
                              rowActions.canResetBlock === true &&
                              item?.blocked === true;
                            const visibleManageUserActionCount = [
                              showEdit,
                              showClientAccess,
                              showTemporaryClientAccess,
                              showPermission,
                              showResetBlock,
                            ].filter(Boolean).length;
                            const manageUserActionGridColsClass =
                              visibleManageUserActionCount <= 2
                                ? "manage-user-action-grid--cols-2"
                                : visibleManageUserActionCount === 3
                                  ? "manage-user-action-grid--cols-3"
                                  : visibleManageUserActionCount === 4
                                    ? "manage-user-action-grid--cols-4"
                                    : "manage-user-action-grid--cols-5";
                            return (
                              <div
                                className={[
                                  "d-inline-grid align-items-center justify-items-center manage-user-action-grid mx-auto",
                                  manageUserActionGridColsClass,
                                ].join(" ")}
                              >
                                {showEdit ? (
                                  <>
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                      onClick={() => onEdit(item)}
                                      id={`manage-user-edit-${item?.id || item?._id || idx}`}
                                    >
                                      <i className="mdi mdi-pencil font-size-18 text-success" aria-hidden="true" />
                                    </span>
                                    <UncontrolledTooltip target={`manage-user-edit-${item?.id || item?._id || idx}`} placement="top">
                                      Edit
                                    </UncontrolledTooltip>
                                  </>
                                ) : null}
                                {showClientAccess ? (
                                  <>
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                      onClick={() => onClientAccess(item)}
                                      id={`manage-user-client-access-${item?.id || item?._id || idx}`}
                                    >
                                      <i className="mdi mdi-office-building font-size-18 text-primary" aria-hidden="true" />
                                    </span>
                                    <UncontrolledTooltip target={`manage-user-client-access-${item?.id || item?._id || idx}`} placement="top">
                                      Client access
                                    </UncontrolledTooltip>
                                  </>
                                ) : null}
                                {showTemporaryClientAccess ? (
                                  <>
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                      onClick={() => onTemporaryClientAccess(item)}
                                      id={`manage-user-temp-client-${item?.id || item?._id || idx}`}
                                    >
                                      <i className="mdi mdi-clock-outline font-size-18 text-warning" aria-hidden="true" />
                                    </span>
                                    <UncontrolledTooltip target={`manage-user-temp-client-${item?.id || item?._id || idx}`} placement="top">
                                      Temporary client access
                                    </UncontrolledTooltip>
                                  </>
                                ) : null}
                                {showPermission ? (
                                  <>
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                      onClick={() => onPermission(item)}
                                      id={`manage-user-permission-${item?.id || item?._id || idx}`}
                                    >
                                      <i className="mdi mdi-shield-key font-size-18 text-info" aria-hidden="true" />
                                    </span>
                                    <UncontrolledTooltip target={`manage-user-permission-${item?.id || item?._id || idx}`} placement="top">
                                      Permission
                                    </UncontrolledTooltip>
                                  </>
                                ) : null}
                                {showResetBlock ? (
                                  <>
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                      onClick={() => onResetBlock(item)}
                                      id={`manage-user-reset-block-${item?.id || item?._id || idx}`}
                                      role="button"
                                    >
                                      <i className="mdi mdi-lock-reset font-size-18 text-danger" aria-hidden="true" />
                                    </span>
                                    <UncontrolledTooltip
                                      target={`manage-user-reset-block-${item?.id || item?._id || idx}`}
                                      placement="top"
                                    >
                                      Reset block
                                    </UncontrolledTooltip>
                                  </>
                                ) : null}
                              </div>
                            );
                          })()}
                          {!isClientStyleTable && pageNameNorm === "airecruiterlog" && (
                            <div className="d-inline-flex align-items-center justify-content-center gap-1 flex-wrap">
                              <span
                                className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                onClick={() => onAiRecruiterCallTranscript?.(item)}
                                id={`ai-recruiter-transcript-${item?.id || item?._id || idx}`}
                                role="button"
                              >
                                <i className="mdi mdi-text-box-outline text-primary" aria-hidden />
                              </span>
                              <UncontrolledTooltip
                                target={`ai-recruiter-transcript-${item?.id || item?._id || idx}`}
                                placement="top"
                              >
                                Call transcript
                              </UncontrolledTooltip>
                              <span
                                className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                onClick={() => onAiRecruiterRawJson?.(item)}
                                id={`ai-recruiter-raw-json-${item?.id || item?._id || idx}`}
                                role="button"
                              >
                                <i className="mdi mdi-code-json text-secondary" aria-hidden />
                              </span>
                              <UncontrolledTooltip
                                target={`ai-recruiter-raw-json-${item?.id || item?._id || idx}`}
                                placement="top"
                              >
                                Raw JSON
                              </UncontrolledTooltip>
                            </div>
                          )}
                          {isCallExecutiveTable && (
                            <div className="d-inline-flex align-items-center justify-content-center gap-1 flex-nowrap">
                              {typeof onCallExecutiveCall === "function" ? (
                                <>
                                  <span
                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                    onClick={() => onCallExecutiveCall(item)}
                                    id={`call-exec-call-${item?.id || item?._id || idx}`}
                                    role="button"
                                  >
                                    <i className="mdi mdi-phone text-success" aria-hidden />
                                  </span>
                                  <UncontrolledTooltip
                                    target={`call-exec-call-${item?.id || item?._id || idx}`}
                                    placement="top"
                                  >
                                    Call
                                  </UncontrolledTooltip>
                                </>
                              ) : null}
                              {typeof onCallExecutiveVideoInterview === "function" &&
                              (!callExecutiveVideoInterviewRequiresLink ||
                                item?.candidateVideoInterviewLink) ? (
                                <>
                                  <span
                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                    onClick={() => onCallExecutiveVideoInterview(item)}
                                    id={`call-exec-video-interview-${item?.id || item?._id || idx}`}
                                    role="button"
                                  >
                                    <i className="mdi mdi-video text-primary" aria-hidden />
                                  </span>
                                  <UncontrolledTooltip
                                    target={`call-exec-video-interview-${item?.id || item?._id || idx}`}
                                    placement="top"
                                  >
                                    Video interview
                                  </UncontrolledTooltip>
                                </>
                              ) : typeof onCallExecutiveMessage === "function" ? (
                                <>
                                  <span
                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                    onClick={() => onCallExecutiveMessage(item)}
                                    id={`call-exec-message-${item?.id || item?._id || idx}`}
                                    role="button"
                                  >
                                    <i className="mdi mdi-message-text text-primary" aria-hidden />
                                  </span>
                                  <UncontrolledTooltip
                                    target={`call-exec-message-${item?.id || item?._id || idx}`}
                                    placement="top"
                                  >
                                    Message
                                  </UncontrolledTooltip>
                                </>
                              ) : null}
                              {typeof onCallExecutiveMarkEligible === "function" ? (
                                <>
                                  <span
                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                    onClick={() => onCallExecutiveMarkEligible(item)}
                                    id={`call-exec-mark-eligible-${item?.id || item?._id || idx}`}
                                    role="button"
                                  >
                                    <i className="mdi mdi-check-decagram text-success" aria-hidden />
                                  </span>
                                  <UncontrolledTooltip
                                    target={`call-exec-mark-eligible-${item?.id || item?._id || idx}`}
                                    placement="top"
                                  >
                                    Mark as eligible
                                  </UncontrolledTooltip>
                                </>
                              ) : null}
                              {typeof onCallExecutiveResolved === "function" ? (
                                <>
                                  <span
                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                    onClick={() => onCallExecutiveResolved(item)}
                                    id={`call-exec-resolved-${item?.id || item?._id || idx}`}
                                    role="button"
                                  >
                                    <i className="mdi mdi-clipboard-check-outline text-success" aria-hidden />
                                  </span>
                                  <UncontrolledTooltip
                                    target={`call-exec-resolved-${item?.id || item?._id || idx}`}
                                    placement="top"
                                  >
                                    Resolved
                                  </UncontrolledTooltip>
                                </>
                              ) : null}
                            </div>
                          )}
                          {tableVariant === "common" && (
                            <div className="d-inline-flex align-items-center justify-content-center gap-1 flex-wrap">
                              {pageNameNorm === "readytosendreport" &&
                                typeof onDelete === "function" &&
                                item?.canDelete && (
                                  <>
                                    <span
                                      className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                      onClick={() => onDelete(item)}
                                      id={`ready-to-send-delete-${item?.id || item?._id || idx}`}
                                      role="button"
                                    >
                                      <i
                                        className="mdi mdi-delete font-size-18 text-danger"
                                        aria-hidden
                                      />
                                    </span>
                                    <UncontrolledTooltip
                                      target={`ready-to-send-delete-${item?.id || item?._id || idx}`}
                                      placement="top"
                                    >
                                      Delete
                                    </UncontrolledTooltip>
                                  </>
                                )}
                              {pageNameNorm === "candidateonboarding" && onOnboardingDocument && (
                                <>
                                  <span
                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                    onClick={() => onOnboardingDocument?.(item)}
                                    id={`candidate-onboarding-doc-${item?.id || item?._id || idx}`}
                                    role="button"
                                  >
                                    <i
                                      className="mdi mdi-file-document-outline font-size-18 text-primary"
                                      aria-hidden
                                    />
                                  </span>
                                  <UncontrolledTooltip
                                    target={`candidate-onboarding-doc-${item?.id || item?._id || idx}`}
                                    placement="top"
                                  >
                                    Onboarding Document
                                  </UncontrolledTooltip>
                                </>
                              )}
                              {isEditAccess && (
                                <>
                                  <span
                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                    onClick={() => onEdit?.(item)}
                                    id={`common-table-edit-${item?.id || item?._id || idx}`}
                                    role="button"
                                  >
                                    <i className="mdi mdi-pencil font-size-18 text-success" aria-hidden />
                                  </span>
                                  <UncontrolledTooltip
                                    target={`common-table-edit-${item?.id || item?._id || idx}`}
                                    placement="top"
                                  >
                                    Edit
                                  </UncontrolledTooltip>
                                </>
                              )}
                              {typeof onPipeline === "function" && (
                                <>
                                  <span
                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                    onClick={() => onPipeline(item)}
                                    id={`common-table-pipeline-${item?.clientId || item?.id || item?._id || idx}`}
                                    role="button"
                                  >
                                    <i className="mdi mdi-pipe font-size-18 text-primary" aria-hidden />
                                  </span>
                                  <UncontrolledTooltip
                                    target={`common-table-pipeline-${item?.clientId || item?.id || item?._id || idx}`}
                                    placement="top"
                                  >
                                    View pipeline
                                  </UncontrolledTooltip>
                                </>
                              )}
                              {isViewAccess && (
                                <>
                                  <span
                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                    onClick={() => onView?.(item)}
                                    id={`common-table-view-${item?.id || item?._id || idx}`}
                                    role="button"
                                  >
                                    <i className="mdi mdi-eye-outline font-size-18 text-primary" aria-hidden />
                                  </span>
                                  <UncontrolledTooltip
                                    target={`common-table-view-${item?.id || item?._id || idx}`}
                                    placement="top"
                                  >
                                    View
                                  </UncontrolledTooltip>
                                </>
                              )}
                              {isCallDetailsAccess && (
                                <>
                                  <span
                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                    onClick={() => onDetails?.(item)}
                                    id={`common-table-details-${item?.id || item?._id || idx}`}
                                    role="button"
                                  >
                                    <i className="mdi mdi-phone-log font-size-18 text-success" aria-hidden />
                                  </span>
                                  <UncontrolledTooltip
                                    target={`common-table-details-${item?.id || item?._id || idx}`}
                                    placement="top"
                                  >
                                    Call details
                                  </UncontrolledTooltip>
                                </>
                              )}
                              {isDeleteAccess && (
                                <>
                                  <span
                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                    onClick={() => onDelete?.(item)}
                                    id={`common-table-delete-${item?.id || item?._id || idx}`}
                                    role="button"
                                  >
                                    <i className="mdi mdi-delete font-size-18 text-danger" aria-hidden />
                                  </span>
                                  <UncontrolledTooltip
                                    target={`common-table-delete-${item?.id || item?._id || idx}`}
                                    placement="top"
                                  >
                                    Delete
                                  </UncontrolledTooltip>
                                </>
                              )}
                            </div>
                          )}
                        </>
                      </td>
                    ) : null}
                  </tr>
                  );
                })}
              </tbody>
            </DragDropContext>
          </Table>
        </div>

        {/* Pagination */}
        {isPagination ?
          <div className="d-flex justify-content-end align-items-center px-3 py-2 border-top bg-white">
            <div className="d-flex align-items-center gap-2 me-3">
              <span className="text-muted small">Rows per page:</span>
              <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                <DropdownToggle caret color="light" size="sm" className="border bg-white">
                  {formatPageSizeLabel(rowsPerPage)}
                </DropdownToggle>
                <DropdownMenu>
                  {paginationSizeOptions.map((num) => (
                    <DropdownItem key={num} onClick={() => handleRowsPerPageChange(num)}>
                      {formatPageSizeLabel(num)}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
            <span className="text-muted small me-3">
              {displayFrom}–{displayTo} of {totalRecordsCount}
            </span>
            <div className="table-pagination-nav">
              <Button
                color="link"
                className="table-pagination-arrow-btn text-muted"
                disabled={currentPage === 1}
                onClick={handlePrevPage}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M15.41 16.58L10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
                </svg>
              </Button>

              {paginationPageItems.map((item, index) =>
                item === PAGINATION_ELLIPSIS ? (
                  <span
                    key={`ellipsis-${index}`}
                    className="table-pagination-ellipsis"
                    aria-hidden="true"
                  >
                    …
                  </span>
                ) : (
                  <Button
                    key={item}
                    color="link"
                    className={`table-pagination-page-btn ${
                      item === currentPage ? "is-active text-primary" : "text-muted"
                    }`}
                    onClick={() => handlePageNumberClick(item)}
                    aria-current={item === currentPage ? "page" : undefined}
                  >
                    {item}
                  </Button>
                )
              )}

              <Button
                color="link"
                className="table-pagination-arrow-btn text-muted"
                disabled={currentPage === totalPages}
                onClick={handleNextPage}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M8.59 16.58L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
                </svg>
              </Button>
            </div>
          </div> : null
        }

        {/* Column Modal */}
        <Modal size="md" isOpen={showColumnModal} toggle={toggleColumnModal} backdrop="static" centered={true} className="modal-full-height"
        >
          <ModalHeader toggle={toggleColumnModal}>Manage Columns</ModalHeader>
          <ModalBody className="p-4 modal-height-fixed">
            <ManageColumnsModalFields
              columns={columns}
              columnOrder={columnOrder}
              orderedColumns={orderedColumns}
              visibleColumns={visibleColumns}
              onToggle={handleColumnToggle}
              tableKey={resolvedTableKey}
              showColumnModal={showColumnModal}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleColumnModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {isSpecialNotesNameClickPage ? (
          <SpecialNotesModal
            isOpen={specialNotesModalOpen}
            onClose={closeSpecialNotesModal}
            candidateName={specialNotesModalContext.candidateName}
            specialNotes={specialNotesModalContext.specialNotes}
          />
        ) : null}
      </CardBody>
    </Card>
  );
};

export default TableContainer;
